import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { openSttStream, transcribeAudio, type SttStream } from '../api/audio';
import { ApiError } from '../api/client';
import { useT } from '../I18nContext';

/**
 * Mic button. Click → request microphone permission → record →
 * **auto-stop on silence (VAD)** → upload to /api/speech-to-text →
 * call `onTranscript` with the recognised text.
 *
 * UX brief: behave like the major voice assistants. Tap once,
 * speak, and stop - no second tap needed. Hard manual-stop
 * remains available for users who want to cut a recording short.
 *
 * Three visible states:
 *   idle          normal mic icon
 *   recording     pulsing red square (tap to stop early)
 *   transcribing  spinner (no further interaction)
 *
 * Voice activity detection
 * ------------------------
 * Web Audio API + AnalyserNode samples the mic level at
 * ~animation-frame cadence. After we hear non-silence at least
 * once (`hasHeardSpeech`), a sustained period below
 * `SILENCE_THRESHOLD` for `SILENCE_DURATION_MS` triggers an
 * auto-stop. A startup grace window stops dead-air recordings
 * (user didn't speak at all) after `STARTUP_GRACE_MS`. Hard
 * cap of `MAX_RECORDING_MS` to bound runaway recordings.
 *
 * Cleanup
 * -------
 * MediaStream tracks AND the AudioContext both need explicit
 * teardown - leaving the AudioContext open keeps the mic
 * indicator on in some browsers. `cleanupAudio` covers both.
 */
type MicState = 'idle' | 'recording' | 'transcribing';

interface MicButtonProps {
  /** Called with the recognised text on a successful recording.
   *  Caller decides what to do with it (fill textarea, auto-send,
   *  etc.). The Composer auto-sends voice transcripts to make
   *  voice-in → voice-out feel like a single gesture. */
  onTranscript: (text: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  /** Active tenant's STT provider. When 'google_streaming', the
   *  button opens a WebSocket alongside the MediaRecorder and emits
   *  interim transcripts via onPartial; the final replaces the
   *  partial via onTranscript. On any streaming failure it falls
   *  back to the existing POST /api/speech-to-text path with the
   *  blob it kept locally. Other values use the POST path. Optional
   *  so callers that don't care still get the legacy behaviour. */
  sttProvider?: 'whisper' | 'google' | 'google_streaming';
  /** Interim transcripts (Google streaming only). Fired with the
   *  latest partial text as the user speaks; an empty string clears
   *  the partial (signals the final has landed or the stream
   *  failed). The Composer typically renders this next to whatever
   *  the user already typed. */
  onPartial?: (text: string) => void;
  /** How long of sustained quiet (ms) after the user has spoken
   *  before VAD auto-stops the recording. Server-resolved by the
   *  resolver chain (client > global > 1200ms floor); the Composer
   *  reads it from /me and passes it down. Optional - omit and the
   *  button falls back to the in-code default. */
  silenceDurationMs?: number;
}

// VAD tuning. These numbers were picked to feel responsive without
// cutting people off mid-thought. The silence duration is now
// configurable - the Composer reads it from /me (which applies
// the client > global > 1200ms in-code chain server-side) and
// passes it down as silenceDurationMs. The other thresholds stay
// hardcoded because they're guard rails, not user preferences:
// SPEECH_RMS sets what counts as "audible" (a calibration constant);
// STARTUP_GRACE cancels dead-air recordings; MAX_RECORDING is a
// safety cap. Bump silenceDurationMs via AI Settings if users
// complain about premature stops.
const SILENCE_THRESHOLD = 0.012;        // 0-1 normalised RMS-ish
const DEFAULT_SILENCE_DURATION_MS = 1200; // matches the server resolver's floor
const STARTUP_GRACE_MS = 6000;          // dead air → cancel
const MAX_RECORDING_MS = 60000;         // hard cap

export function MicButton({
  onTranscript,
  onError,
  disabled,
  sttProvider,
  onPartial,
  silenceDurationMs,
}: MicButtonProps) {
  const t = useT();
  // Resolved silence window. Falls back to the in-code default when
  // the prop is missing or out of the supported [300, 5000] window
  // so a stale /me response can't push auto-stop to "instant" or
  // "essentially never".
  const silenceMs = (() => {
    const v = silenceDurationMs;
    if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULT_SILENCE_DURATION_MS;
    if (v < 300 || v > 5000) return DEFAULT_SILENCE_DURATION_MS;
    return Math.round(v);
  })();
  const [state, setState] = useState<MicState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const vadRafRef = useRef<number | null>(null);
  // Streaming STT plumbing. Only populated when the tenant resolves
  // to 'google_streaming'. We forward MediaRecorder chunks here AND
  // keep them in chunksRef so a stream failure can fall back to the
  // existing POST path with the full blob.
  const sttStreamRef = useRef<SttStream | null>(null);
  const sttStreamFailedRef = useRef(false);

  // Cleanup on unmount: stop the mic + AudioContext, AND cancel any
  // open streaming WS so a closing route doesn't leave the worker
  // thread hanging on a never-arriving final.
  useEffect(() => {
    return () => {
      cleanupAudio();
      try { sttStreamRef.current?.cancel(); } catch { /* noop */ }
      sttStreamRef.current = null;
    };
  }, []);

  function cleanupAudio() {
    if (vadRafRef.current !== null) {
      cancelAnimationFrame(vadRafRef.current);
      vadRafRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      void audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    // Don't cancel the SttStream here - handleStop owns it (awaits
    // the final), and unmount cleanup runs cancel() separately.
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Some browsers ignore the mimeType hint and pick their own
      // default. We pass webm-Opus where supported; the server's
      // STT handler accepts webm/mp4/ogg.
      const mime =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = recorder;

      // Streaming path: open a WebSocket in parallel when the tenant
      // is on 'google_streaming' AND the recorder container is one
      // Google's streaming parser accepts. Safari emits audio/mp4
      // which Google doesn't decode, so it transparently falls back
      // to the POST endpoint by skipping the WS entirely.
      sttStreamRef.current = null;
      sttStreamFailedRef.current = false;
      const containerOk = !!mime && (mime.includes('webm') || mime.includes('ogg'));
      if (sttProvider === 'google_streaming' && containerOk) {
        try {
          const sttStream = openSttStream({ contentType: mime || 'audio/webm' });
          sttStream.onPartial((text) => onPartial?.(text));
          // If the handshake fails before any audio is sent, flag the
          // stream as dead so handleStop uses the POST fallback. The
          // mic itself still works fine - the user just doesn't get
          // partials for this utterance.
          sttStream.opened.catch((err) => {
            console.warn('STT stream failed to open:', err);
            sttStreamFailedRef.current = true;
          });
          sttStreamRef.current = sttStream;
        } catch (err) {
          console.warn('Could not open STT stream:', err);
          sttStreamFailedRef.current = true;
        }
      }

      recorder.addEventListener('dataavailable', (ev) => {
        if (!ev.data || ev.data.size === 0) return;
        chunksRef.current.push(ev.data);
        // Forward to the WS while it's healthy. We don't await the
        // arrayBuffer() promise here - SttStream queues it internally
        // so chunks arrive in order at Google.
        const s = sttStreamRef.current;
        if (s && !sttStreamFailedRef.current) {
          try {
            s.send(ev.data);
          } catch (err) {
            console.warn('STT stream send failed:', err);
            sttStreamFailedRef.current = true;
          }
        }
      });
      recorder.addEventListener('stop', handleStop);
      // timeslice=250 so MediaRecorder emits chunks every 250 ms.
      // Without a timeslice the entire utterance arrives as one
      // chunk on stop - fine for the POST path but useless for
      // streaming partials. The POST path reassembles chunks the
      // same way either way.
      recorder.start(250);
      setState('recording');

      // Hook up VAD. Done after recorder.start so any analyser
      // failure doesn't prevent recording - manual tap-to-stop
      // is the fallback in that case.
      try {
        startVad(stream);
      } catch (err) {
        // Non-fatal: just won't auto-stop.
        console.warn('VAD setup failed; manual stop still works', err);
      }
    } catch (err) {
      // getUserMedia rejections - usually NotAllowedError (denied)
      // or NotFoundError (no mic). Both surface the same way.
      console.warn('Mic start failed', err);
      onError(
        'Could not access the microphone. Check that this site has ' +
          'mic permission and try again.',
      );
      setState('idle');
    }
  }

  function startVad(stream: MediaStream) {
    // Construct AudioContext lazily - some browsers (Safari)
    // require user-gesture initiation, which the click already
    // satisfies.
    const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return; // No Web Audio API - skip VAD, manual stop only.
    const ctx = new Ctx();
    audioContextRef.current = ctx;

    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);

    const buf = new Uint8Array(analyser.frequencyBinCount);
    const startedAt = performance.now();
    let silenceStart: number | null = null;
    let hasHeardSpeech = false;

    const tick = () => {
      // Stop the loop if we're no longer recording (handleStop ran).
      if (!recorderRef.current || recorderRef.current.state !== 'recording') {
        vadRafRef.current = null;
        return;
      }

      analyser.getByteFrequencyData(buf);
      // Average normalised level. Not a true RMS but cheap and
      // good enough as a "is anyone speaking?" signal.
      let sum = 0;
      for (const v of buf) sum += v;
      const level = sum / buf.length / 255;
      const now = performance.now();
      const elapsed = now - startedAt;

      // Hard cap - bail no matter what.
      if (elapsed > MAX_RECORDING_MS) {
        stopRecording();
        return;
      }

      if (level >= SILENCE_THRESHOLD) {
        hasHeardSpeech = true;
        silenceStart = null;
      } else if (hasHeardSpeech) {
        // We've heard speech, now waiting for sustained silence.
        if (silenceStart === null) silenceStart = now;
        else if (now - silenceStart > silenceMs) {
          stopRecording();
          return;
        }
      } else if (elapsed > STARTUP_GRACE_MS) {
        // Recorded dead air for > grace window. User probably
        // tapped by accident or the mic isn't picking up.
        stopRecording();
        return;
      }

      vadRafRef.current = requestAnimationFrame(tick);
    };
    vadRafRef.current = requestAnimationFrame(tick);
  }

  function stopRecording() {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
      // setState happens in handleStop after the dataavailable
      // event fires.
    }
  }

  async function handleStop() {
    cleanupAudio();

    const blob = new Blob(chunksRef.current, {
      type: chunksRef.current[0]?.type || 'audio/webm',
    });
    chunksRef.current = [];

    // Tiny / empty blob → user said nothing. Bail without going
    // to the server (saves a Whisper call). Also cancels any open
    // streaming session for the same reason.
    if (blob.size < 800) {
      try { sttStreamRef.current?.cancel(); } catch { /* noop */ }
      sttStreamRef.current = null;
      onPartial?.(''); // clear any stale partial display
      setState('idle');
      return;
    }

    setState('transcribing');

    // Streaming path: if the WebSocket is alive and the handshake
    // succeeded, signal end-of-audio and wait for the final
    // transcript. Any failure falls through to the POST endpoint
    // with the blob we kept in parallel - the user still gets a
    // transcript for the same utterance.
    const sttStream = sttStreamRef.current;
    sttStreamRef.current = null;
    if (sttStream && !sttStreamFailedRef.current) {
      try {
        const finalText = (await sttStream.end()).trim();
        // Clear the in-flight partial before delivering the final;
        // the composer rebuilds its display from the final via
        // onTranscript.
        onPartial?.('');
        if (finalText) {
          onTranscript(finalText);
        } else {
          onError(
            'Could not understand the recording. Try speaking a bit louder ' +
              'or moving closer to the mic.',
          );
        }
        setState('idle');
        return;
      } catch (err) {
        console.warn('Streaming STT final failed, falling back to POST:', err);
        onPartial?.(''); // wipe stale partial; final via POST replaces it
        sttStreamFailedRef.current = true;
      }
    } else if (sttStream) {
      try { sttStream.cancel(); } catch { /* noop */ }
      onPartial?.('');
    }

    try {
      const result = await transcribeAudio(blob);
      const text = (result.text || '').trim();
      if (text) {
        onTranscript(text);
      } else {
        onError(
          'Could not understand the recording. Try speaking a bit louder ' +
            'or moving closer to the mic.',
        );
      }
    } catch (err) {
      onError(
        err instanceof ApiError
          ? err.message
          : 'Speech-to-text failed. Please try again.',
      );
    } finally {
      setState('idle');
    }
  }

  function handleClick() {
    if (disabled) return;
    if (state === 'idle') {
      void startRecording();
    } else if (state === 'recording') {
      stopRecording();
    }
    // transcribing → no-op (button is disabled visually)
  }

  const label =
    state === 'recording'
      ? t('mic.stop')
      : state === 'transcribing'
        ? t('mic.transcribing')
        : t('mic.record');

  return (
    <button
      type="button"
      className={
        'composer-icon-btn' +
        (state === 'recording' ? ' is-recording' : '') +
        (state === 'transcribing' ? ' is-busy' : '')
      }
      onClick={handleClick}
      disabled={disabled || state === 'transcribing'}
      title={label}
      aria-label={label}
      aria-pressed={state === 'recording'}
    >
      {state === 'recording' ? <Square size={14} /> : <Mic size={16} />}
    </button>
  );
}
